import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tabs,
  Tab,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PlayerRankingWidget from '../../components/stats/PlayerRankingWidget';
import TeamRankingWidget from '../../components/stats/TeamRankingWidget';
import StandingsWidget from '../../components/stats/StandingsWidget';
import ResultsWidget from '../../components/stats/ResultsWidget';

import {
  getCompetitionsForRankingBuilder,
  getCompetitionSeasonsForRankingBuilder,
  getRankingCompetitionMeta,
  getWidgetConfiguration,
} from '../../../api';

const DISCIPLINE_LABELS = {
  rugby: 'Rugby',
  soccer: 'Football',
};

const DISCIPLINE_OPTIONS = [
  { value: 'rugby', label: 'Rugby' },
  { value: 'soccer', label: 'Football' },
];

const TAB_LABELS = {
  player_ranking: 'Statistiques joueurs',
  team_ranking: 'Statistiques équipes',
  team_standings: 'Classement',
  competition_results: 'Résultats',
};

const getSeasonDisplayLabel = (seasonValue) => {
  const value = String(seasonValue || '').trim();
  if (!value) return '';

  if (value.includes('/')) {
    return value.split('/')[0];
  }

  return value;
};

const normalizeSeasonOptions = (rawSeasons = []) => {
  const byLabel = new Map();

  rawSeasons.forEach((seasonValue) => {
    const value = String(seasonValue || '').trim();
    if (!value) return;

    const label = getSeasonDisplayLabel(value);
    const existing = byLabel.get(label);

    if (!existing) {
      byLabel.set(label, { value, label });
      return;
    }

    if (!existing.value.includes('/') && value.includes('/')) {
      byLabel.set(label, { value, label });
    }
  });

  return Array.from(byLabel.values()).sort((a, b) =>
    b.label.localeCompare(a.label)
  );
};

const parsePositiveDay = (value) => {
  if (value == null || value === '') return '';
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return '';
  return String(Math.floor(parsed));
};

const normalizeDayValue = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized !== '' ? normalized : null;
};

export default function ComposedTabsBuilderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [widgetConfig, setWidgetConfig] = useState(null);
  const [widgetConfigLoading, setWidgetConfigLoading] = useState(false);
  const [widgetConfigError, setWidgetConfigError] = useState('');

  const provider = searchParams.get('provider') || '';
  const discipline = searchParams.get('discipline') || '';
  const seasonParam = searchParams.get('season') || '';
  const seasonSinceParam = searchParams.get('seasonSince') || '';
  const competitionIdsParam = searchParams.get('competitionIds') || '';
  const dayParam = searchParams.get('day') || '';
  const tabParam = searchParams.get('tab') || '';
  const widgetConfigurationId = searchParams.get('widgetConfigurationId') || '';

  const [seasonOptions, setSeasonOptions] = useState([]);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);

  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitions, setSelectedCompetitions] = useState([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(false);
  const [competitionsError, setCompetitionsError] = useState('');

  const [competitionInfos, setCompetitionInfos] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [maxDay, setMaxDay] = useState(null);
  const [dayInput, setDayInput] = useState(parsePositiveDay(dayParam));

  const selectedCompetitionIds = useMemo(
    () =>
      competitionIdsParam
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    [competitionIdsParam]
  );

  const selectedSeasonsFromUrl = useMemo(
    () =>
      seasonParam
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [seasonParam]
  );

  const widgetSeason =
    selectedSeasonsFromUrl.length > 0
      ? selectedSeasonsFromUrl.join(',')
      : null;

  const childWidgets = useMemo(() => {
    if (Array.isArray(widgetConfig?.childWidgets) && widgetConfig.childWidgets.length) {
      return [...widgetConfig.childWidgets].sort(
        (a, b) => Number(a?.order || 0) - Number(b?.order || 0)
      );
    }

    if (Array.isArray(widgetConfig?.childWidgetTypes) && widgetConfig.childWidgetTypes.length) {
      console.log('childWidgetTypes', widgetConfig.childWidgetTypes);
      return widgetConfig.childWidgetTypes.map((widgetType, index) => ({
        widgetType,
        label: TAB_LABELS[widgetType] || widgetType,
        order: index,
        widgetConfigurationId: null,
      }));
    }

    return [];
  }, [widgetConfig]);

  const availableTabValues = useMemo(
    () => childWidgets.map((child) => child.widgetType).filter(Boolean),
    [childWidgets]
  );

  const activeTab = useMemo(() => {
    if (tabParam && availableTabValues.includes(tabParam)) {
      return tabParam;
    }
    return availableTabValues[0] || '';
  }, [tabParam, availableTabValues]);

  const activeChildWidget = useMemo(
    () => childWidgets.find((child) => child.widgetType === activeTab) || null,
    [childWidgets, activeTab]
  );

  const activeWidgetConfigurationId =
    activeChildWidget?.widgetConfigurationId
      ? String(activeChildWidget.widgetConfigurationId)
      : null;

  const allowedDisciplines = useMemo(() => {
    if (Array.isArray(widgetConfig?.disciplines) && widgetConfig.disciplines.length) {
      return widgetConfig.disciplines;
    }
    if (widgetConfig?.discipline) {
      return [widgetConfig.discipline];
    }
    return [];
  }, [widgetConfig]);

  const effectiveProvider = widgetConfig?.provider || provider || '';

  const effectiveDiscipline = useMemo(() => {
    if (!allowedDisciplines.length) {
      return discipline || '';
    }

    if (discipline && allowedDisciplines.includes(discipline)) {
      return discipline;
    }

    if (
      widgetConfig?.defaultDiscipline &&
      allowedDisciplines.includes(widgetConfig.defaultDiscipline)
    ) {
      return widgetConfig.defaultDiscipline;
    }

    return allowedDisciplines[0];
  }, [allowedDisciplines, discipline, widgetConfig]);

  const disciplineConfig = useMemo(() => {
  return widgetConfig?.filtersByDiscipline?.[effectiveDiscipline] || {};
}, [widgetConfig, effectiveDiscipline]);

    const seasonMode = disciplineConfig?.seasonMode === 'since' ? 'since' : 'explicit';

    const configuredSeasonSince = useMemo(() => {
    return seasonMode === 'since' && disciplineConfig?.seasonSince
        ? String(disciplineConfig.seasonSince).trim()
        : '';
    }, [disciplineConfig, seasonMode]);

    const allowedSeasonValues = useMemo(
    () => new Set((disciplineConfig?.allowedSeasons || []).map((v) => String(v).trim()).filter(Boolean)),
    [disciplineConfig]
    );

    const defaultSeasonValues = useMemo(
    () => (disciplineConfig?.defaultSeasons || []).map((v) => String(v).trim()).filter(Boolean),
    [disciplineConfig]
    );

    const allowedCompetitionIds = useMemo(
    () => new Set((disciplineConfig?.allowedCompetitionIds || []).map((v) => String(v).trim()).filter(Boolean)),
    [disciplineConfig]
    );

    const defaultCompetitionIds = useMemo(
    () => (disciplineConfig?.defaultCompetitionIds || []).map((v) => String(v).trim()).filter(Boolean),
    [disciplineConfig]
    );

  const isMultiCompetition = selectedCompetitionIds.length > 1;
  const showDayFilter = activeTab === 'team_standings';

  useEffect(() => {
    setDayInput(parsePositiveDay(dayParam));
  }, [dayParam]);

  useEffect(() => {
    let ignore = false;

    const loadWidgetConfig = async () => {
      if (!widgetConfigurationId) {
        if (!ignore) {
          setWidgetConfig(null);
          setWidgetConfigError('widgetConfigurationId est requis.');
        }
        return;
      }

      try {
        setWidgetConfigLoading(true);
        setWidgetConfigError('');

        const { data } = await getWidgetConfiguration(widgetConfigurationId);

        if (ignore) return;

        if (!data) {
          setWidgetConfig(null);
          setWidgetConfigError('Configuration widget introuvable.');
          return;
        }

        if (data.widgetType !== 'composed_tabs') {
          setWidgetConfig(null);
          setWidgetConfigError("Cette configuration n'est pas un widget composé.");
          return;
        }

        setWidgetConfig(data);
      } catch (e) {
        if (!ignore) {
          setWidgetConfig(null);
          setWidgetConfigError('Impossible de charger la configuration du widget composé.');
        }
      } finally {
        if (!ignore) {
          setWidgetConfigLoading(false);
        }
      }
    };

    loadWidgetConfig();

    return () => {
      ignore = true;
    };
  }, [widgetConfigurationId]);

  useEffect(() => {
    if (!activeTab) return;
    if (tabParam === activeTab) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', activeTab);
    setSearchParams(nextParams, { replace: true });
  }, [activeTab, tabParam, searchParams, setSearchParams]);

  useEffect(() => {
    let ignore = false;

    const loadSeasons = async () => {
      if (!effectiveProvider || !effectiveDiscipline) {
        setSeasonOptions([]);
        setSelectedSeasons([]);
        return;
      }

      if (seasonMode === 'since') {
            setSeasonOptions([]);
            setSelectedSeasons([]);

            if (configuredSeasonSince && seasonSinceParam !== configuredSeasonSince) {
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete('season');
                nextParams.set('seasonSince', configuredSeasonSince);

                if (effectiveProvider) nextParams.set('provider', effectiveProvider);
                if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
                if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
                if (activeTab) nextParams.set('tab', activeTab);

                setSearchParams(nextParams, { replace: true });
            }

            return;
            }

      try {
        setLoadingSeasons(true);

        const { data } = await getCompetitionSeasonsForRankingBuilder({
          provider: effectiveProvider,
          discipline: effectiveDiscipline,
        });

        if (ignore) return;

        const rawSeasons = Array.isArray(data) ? data : [];
        const normalizedSeasons = normalizeSeasonOptions(rawSeasons).filter((option) =>
            allowedSeasonValues.size === 0 || allowedSeasonValues.has(option.value)
            );

        setSeasonOptions(normalizedSeasons);

        const preselectedFromUrl = normalizedSeasons.filter((option) =>
            selectedSeasonsFromUrl.includes(option.value)
            );

            if (preselectedFromUrl.length) {
            setSelectedSeasons(preselectedFromUrl);

            if (preselectedFromUrl.length !== selectedSeasonsFromUrl.length) {
                const nextParams = new URLSearchParams(searchParams);
                nextParams.set('season', preselectedFromUrl.map((option) => option.value).join(','));
                if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
                if (activeTab) nextParams.set('tab', activeTab);
                setSearchParams(nextParams, { replace: true });
            }
            } else if (normalizedSeasons.length) {
            const configuredDefaults = normalizedSeasons.filter((option) =>
                defaultSeasonValues.includes(option.value)
            );

            const nextSelected = configuredDefaults.length ? configuredDefaults : [normalizedSeasons[0]];
            setSelectedSeasons(nextSelected);

            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('season', nextSelected.map((option) => option.value).join(','));
            nextParams.delete('seasonSince');
            if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
            if (activeTab) nextParams.set('tab', activeTab);
            setSearchParams(nextParams, { replace: true });
            }


      } catch (error) {
        if (!ignore) {
          setSeasonOptions([]);
          setSelectedSeasons([]);
        }
      } finally {
        if (!ignore) {
          setLoadingSeasons(false);
        }
      }
    };

    loadSeasons();

    return () => {
      ignore = true;
    };
  },  [
  effectiveProvider,
  effectiveDiscipline,
  selectedSeasonsFromUrl,
  seasonSinceParam,
  searchParams,
  setSearchParams,
  widgetConfigurationId,
  activeTab,
  seasonMode,
  configuredSeasonSince,
  allowedSeasonValues,
  defaultSeasonValues,
]);

  useEffect(() => {
    let ignore = false;

    const loadCompetitions = async () => {
      if (!effectiveProvider || !effectiveDiscipline) {
        setCompetitions([]);
        setSelectedCompetitions([]);
        return;
      }

      try {
        setLoadingCompetitions(true);
        setCompetitionsError('');
        const effectiveSeasonSince =
            seasonMode === 'since' ? configuredSeasonSince || undefined : undefined;

        const { data } = await getCompetitionsForRankingBuilder({
        provider: effectiveProvider,
        discipline: effectiveDiscipline,
        season:
            seasonMode !== 'since' && selectedSeasons.length
            ? selectedSeasons.map((option) => option.value).join(',')
            : undefined,
        seasonSince: effectiveSeasonSince,
        });

        if (ignore) return;

        const normalized = (Array.isArray(data) ? data : []).filter((competition) =>
            allowedCompetitionIds.size === 0 ||
            allowedCompetitionIds.has(String(competition._id))
            );
        setCompetitions(normalized);

        const preselected = normalized.filter((competition) =>
            selectedCompetitionIds.includes(String(competition._id))
            );

            setSelectedCompetitions(preselected);

            if (selectedCompetitionIds.length && preselected.length !== selectedCompetitionIds.length) {
            const nextParams = new URLSearchParams(searchParams);

            if (effectiveProvider) nextParams.set('provider', effectiveProvider);
            if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
            if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
            if (activeTab) nextParams.set('tab', activeTab);

            if (selectedSeasons.length) {
                nextParams.set(
                'season',
                selectedSeasons.map((option) => option.value).join(',')
                );
            } else if (seasonSinceParam) {
                nextParams.set('seasonSince', seasonSinceParam);
            }

            if (preselected.length) {
                nextParams.set(
                'competitionIds',
                preselected.map((competition) => String(competition._id)).join(',')
                );
            } else {
                const defaultCompetitions = normalized.filter((competition) =>
                defaultCompetitionIds.includes(String(competition._id))
                );

                if (defaultCompetitions.length) {
                nextParams.set(
                    'competitionIds',
                    defaultCompetitions.map((competition) => String(competition._id)).join(',')
                );
                } else {
                nextParams.delete('competitionIds');
                }
            }

            setSearchParams(nextParams, { replace: true });
            } else if (!selectedCompetitionIds.length) {
            const defaultCompetitions = normalized.filter((competition) =>
                defaultCompetitionIds.includes(String(competition._id))
            );

            if (defaultCompetitions.length) {
                setSelectedCompetitions(defaultCompetitions);

                const nextParams = new URLSearchParams(searchParams);
                if (effectiveProvider) nextParams.set('provider', effectiveProvider);
                if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
                if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
                if (activeTab) nextParams.set('tab', activeTab);

                if (selectedSeasons.length) {
                nextParams.set(
                    'season',
                    selectedSeasons.map((option) => option.value).join(',')
                );
                } else if (seasonSinceParam) {
                nextParams.set('seasonSince', seasonSinceParam);
                }

                nextParams.set(
                'competitionIds',
                defaultCompetitions.map((competition) => String(competition._id)).join(',')
                );

                setSearchParams(nextParams, { replace: true });
            }
            }


      } catch (error) {
        if (!ignore) {
          setCompetitions([]);
          setSelectedCompetitions([]);
          setCompetitionsError('Impossible de charger les compétitions.');
        }
      } finally {
        if (!ignore) {
          setLoadingCompetitions(false);
        }
      }
    };

    loadCompetitions();

    return () => {
      ignore = true;
    };
  }, [
  effectiveProvider,
  effectiveDiscipline,
  selectedSeasons,
  seasonSinceParam,
  selectedCompetitionIds,
  allowedCompetitionIds,
  defaultCompetitionIds,
  seasonMode,
  configuredSeasonSince,
  searchParams,
  setSearchParams,
  widgetConfigurationId,
  activeTab,
]);

  useEffect(() => {
    let ignore = false;

    const loadMeta = async () => {
      const firstCompetitionId = selectedCompetitionIds[0];

      if (!firstCompetitionId) {
        if (!ignore) {
          setCompetitionInfos([]);
          setMetaLoading(false);
          setMaxDay(null);
        }
        return;
      }

      try {
        setMetaLoading(true);

        const metas = await Promise.all(
          selectedCompetitionIds.map(async (competitionId) => {
            const { data } = await getRankingCompetitionMeta(competitionId);
            return data;
          })
        );

        if (ignore) return;

        const normalizedInfos = metas
          .map((meta, index) => ({
            id: selectedCompetitionIds[index],
            name: meta?.name || '',
            season: meta?.season || '',
            nbDays: meta?.nbDays ?? null,
            defaultDay: meta?.defaultDay ?? null,
            rounds: Array.isArray(meta?.rounds) ? meta.rounds : [],
          }))
          .filter((item) => item.id);

        setCompetitionInfos(normalizedInfos);

        if (selectedCompetitionIds.length === 1 &&
          activeTab === 'team_standings') {
          const nbDays = Number(normalizedInfos?.[0]?.nbDays);
          const normalizedMaxDay =
            Number.isFinite(nbDays) && nbDays > 0 ? nbDays : null;

          setMaxDay(normalizedMaxDay);

          const parsedDay = parsePositiveDay(dayParam);

          if (!parsedDay && normalizedMaxDay) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('day', String(normalizedMaxDay));
            if (widgetConfigurationId) {
              nextParams.set('widgetConfigurationId', widgetConfigurationId);
            }
            if (activeTab) {
              nextParams.set('tab', activeTab);
            }
            setSearchParams(nextParams, { replace: true });
          } else if (
            parsedDay &&
            normalizedMaxDay &&
            Number(parsedDay) > normalizedMaxDay
          ) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('day', String(normalizedMaxDay));
            if (widgetConfigurationId) {
              nextParams.set('widgetConfigurationId', widgetConfigurationId);
            }
            if (activeTab) {
              nextParams.set('tab', activeTab);
            }
            setSearchParams(nextParams, { replace: true });
          }
        } else {
          setMaxDay(null);
        }
      } catch (error) {
        if (!ignore) {
          setCompetitionInfos([]);
          setMaxDay(null);
        }
      } finally {
        if (!ignore) {
          setMetaLoading(false);
        }
      }
    };

    loadMeta();

    return () => {
      ignore = true;
    };
  }, [
    selectedCompetitionIds,
    activeTab,
    dayParam,
    searchParams,
    setSearchParams,
    widgetConfigurationId,
  ]);

  const handleDisciplineChange = (event) => {
    const value = event.target.value;

    const nextParams = new URLSearchParams();
    if (effectiveProvider) nextParams.set('provider', effectiveProvider);
    if (value) nextParams.set('discipline', value);
    if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
    if (activeTab) nextParams.set('tab', activeTab);

    setSelectedSeasons([]);
    setSelectedCompetitions([]);
    setCompetitionInfos([]);
    setDayInput('');
    setMaxDay(null);

    setSearchParams(nextParams);
  };

  const handleSeasonsChange = (_, value) => {
    setSelectedSeasons(value);
    setSelectedCompetitions([]);
    setCompetitionInfos([]);
    setDayInput('');
    setMaxDay(null);

    const nextParams = new URLSearchParams();
    if (effectiveProvider) nextParams.set('provider', effectiveProvider);
    if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
    if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
    if (activeTab) nextParams.set('tab', activeTab);

    if (value.length) {
      nextParams.set(
        'season',
        value.map((option) => option.value).join(',')
      );
    }

    if (value.length === 0 && seasonSinceParam) {
      nextParams.set('seasonSince', seasonSinceParam);
    }

    setSearchParams(nextParams);
  };

  const handleCompetitionsChange = (_, value) => {
    setSelectedCompetitions(value);

    const nextParams = new URLSearchParams();
    if (effectiveProvider) nextParams.set('provider', effectiveProvider);
    if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
    if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
    if (activeTab) nextParams.set('tab', activeTab);

    if (selectedSeasons.length) {
      nextParams.set(
        'season',
        selectedSeasons.map((option) => option.value).join(',')
      );
    }

    if (!selectedSeasons.length && seasonSinceParam) {
      nextParams.set('seasonSince', seasonSinceParam);
    }

    if (value.length) {
      nextParams.set(
        'competitionIds',
        value.map((competition) => String(competition._id)).join(',')
      );
    }

    if (value.length === 1 && dayParam && showDayFilter) {
      nextParams.set('day', dayParam);
    }

    setSearchParams(nextParams);
  };

  const handleTabChange = (_, nextTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', nextTab);

    if (nextTab !== 'team_standings' && nextTab !== 'competition_results') {
      nextParams.delete('day');
    }

    setSearchParams(nextParams, { replace: true });
  };

  const handleDayChange = (event) => {
    const value = event.target.value.replace(/[^\d]/g, '');
    setDayInput(value);
  };

  const handleDayBlur = () => {
    const normalized = parsePositiveDay(dayInput);
    const nextParams = new URLSearchParams(searchParams);

    if (!normalized) {
      nextParams.delete('day');
      setSearchParams(nextParams, { replace: true });
      return;
    }

    let nextDay = Number(normalized);
    if (maxDay && nextDay > maxDay) {
      nextDay = maxDay;
    }

    nextParams.set('day', String(nextDay));
    setSearchParams(nextParams, { replace: true });
  };

  const handleDaySelectChange = (event) => {
    const value = parsePositiveDay(event.target.value);
    const nextParams = new URLSearchParams(searchParams);

    if (!value) {
      nextParams.delete('day');
    } else {
      nextParams.set('day', value);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const handleWidgetDayChange = (nextDay) => {
    const nextParams = new URLSearchParams(searchParams);
    const normalizedDay = normalizeDayValue(nextDay);

    if (!normalizedDay) {
      nextParams.delete('day');
    } else {
      nextParams.set('day', normalizedDay);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const currentDay =
    parsePositiveDay(dayParam) || (maxDay ? String(maxDay) : '');

  const dayOptions = useMemo(() => {
    if (!maxDay) return [];
    return Array.from({ length: maxDay }, (_, index) => {
      const value = index + 1;
      return { value: String(value), label: `Journée ${value}` };
    }).reverse();
  }, [maxDay]);

  const canRenderWidget =
    Boolean(effectiveDiscipline) &&
    Boolean(activeTab) &&
    selectedCompetitionIds.length > 0;

  const renderActiveWidget = () => {
    if (!activeTab) return null;

    switch (activeTab) {
      case 'player_ranking':
        return (
          <PlayerRankingWidget
            provider={effectiveProvider}
            discipline={effectiveDiscipline}
            season={widgetSeason}
            widgetConfigurationId={activeWidgetConfigurationId}
            competitionIds={selectedCompetitionIds}
            title={
              selectedCompetitionIds.length > 1
                ? 'Statistiques joueurs'
                : competitionInfos?.[0]?.name || 'Statistiques joueurs'
            }
            onNavigate={(url) => navigate(url)}
          />
        );

      case 'team_ranking':
        return (
          <TeamRankingWidget
            provider={effectiveProvider}
            discipline={effectiveDiscipline}
            season={widgetSeason}
            widgetConfigurationId={activeWidgetConfigurationId}
            competitionIds={selectedCompetitionIds}
            title={
              selectedCompetitionIds.length > 1
                ? 'Statistiques équipes'
                : competitionInfos?.[0]?.name || 'Statistiques équipes'
            }
            onNavigate={(url) => navigate(url)}
          />
        );

      case 'team_standings':
        return (
          <StandingsWidget
            provider={effectiveProvider}
            discipline={effectiveDiscipline}
            competitionIds={selectedCompetitionIds}
            widgetConfigurationId={activeWidgetConfigurationId}
            day={!isMultiCompetition && currentDay ? Number(currentDay) : null}
            title=""
            onNavigate={(url) => navigate(url)}
          />
        );

      case 'competition_results':
        return (
          <ResultsWidget
            provider={effectiveProvider}
            discipline={effectiveDiscipline}
            competitionIds={selectedCompetitionIds}
            competitionInfos={competitionInfos}
            day={selectedCompetitionIds.length === 1 ? normalizeDayValue(dayParam) : null}
            widgetConfigurationId={activeWidgetConfigurationId || null}
            title=""
            onNavigate={(url) => navigate(url)}
            onDayChange={selectedCompetitionIds.length === 1 ? handleWidgetDayChange : undefined}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: '#e5e7eb',
              p: { xs: 2, md: 3 },
            }}
          >
            <Stack spacing={2}>
              {widgetConfig?.configName && <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {widgetConfig?.configName}
                </Typography>
              </Box>}

              {widgetConfigError ? (
                <Alert severity="error">{widgetConfigError}</Alert>
              ) : null}

              {widgetConfigLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : null}

              {availableTabValues.length > 0 ? (
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {childWidgets.map((child) => (
                    <Tab
                      key={child.widgetType}
                      value={child.widgetType}
                      label={TAB_LABELS[child.widgetType] || child.widgetType}
                    />
                  ))}
                </Tabs>
              ) : null}

              {allowedDisciplines.length > 1 ? (
                <TextField
                  select
                  fullWidth
                  label="Sport"
                  value={effectiveDiscipline}
                  onChange={handleDisciplineChange}
                >
                  {DISCIPLINE_OPTIONS
                    .filter((option) => allowedDisciplines.includes(option.value))
                    .map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                </TextField>
              ) : null}

              {seasonMode !== 'since' ? (
                <Autocomplete
                  multiple
                  options={seasonOptions}
                  value={selectedSeasons}
                  loading={loadingSeasons}
                  onChange={handleSeasonsChange}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Saisons"
                      placeholder="Sélectionner une ou plusieurs saisons"
                    />
                  )}
                />
              ) : null}

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={competitions}
                value={selectedCompetitions}
                loading={loadingCompetitions}
                onChange={handleCompetitionsChange}
                getOptionLabel={(option) =>
                  option?.season
                    ? `${option.name} (${option.season})`
                    : option?.name || ''
                }
                isOptionEqualToValue={(option, value) =>
                  String(option._id) === String(value._id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Compétitions"
                    placeholder="Sélectionner une ou plusieurs compétitions"
                  />
                )}
              />

              {showDayFilter && selectedCompetitionIds.length === 1 ? (
                maxDay ? (
                  <TextField
                    select
                    fullWidth
                    label="Journée"
                    value={currentDay}
                    onChange={handleDaySelectChange}
                  >
                    {dayOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    fullWidth
                    label="Journée"
                    value={dayInput}
                    onChange={handleDayChange}
                    onBlur={handleDayBlur}
                    placeholder="Ex. 18"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                )
              ) : null}

              {competitionsError ? (
                <Alert severity="error">{competitionsError}</Alert>
              ) : null}
            </Stack>
          </Paper>

          {canRenderWidget ? (
            metaLoading && (activeTab === 'team_standings' || activeTab === 'competition_results') ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              renderActiveWidget()
            )
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}